import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { MATTRACKERAPIConstants } from '../constants/constants';
import { SharedDataService } from './shared-data.service';

export interface StorageHubPayload {
  location_id?: string;
  source_system_operational_id?: string;
  location_maximo_id?: string;
  location_name: string;
  location_type?: string;
  location_desc?: string;
  location_latitude_value?: number;
  location_longitude_value?: number;
  is_direct_update_allowed?: boolean;
  is_transfer_allowed?: boolean;
  is_mat_allocation_source?: boolean;
  is_location_active?: boolean;
  is_current?: boolean;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
}

export interface StorageHub {
  LocationId?: number;
  LocationMaximoId?: string;
  LocationName: string;
  LocationType?: string;
  LocationDesc?: string;
  LocationLatitudeValue?: number;
  LocationLongitudeValue?: number;
  IsDirectUpdateAllowed?: string;
  IsTransferAllowed?: string;
  IsMatAllocationSource?: string;
  IsLocationActive?: string;
  CreatedAt?: string;
  CreatedBy?: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
  available?: number;
}

export interface LookupData {
  ReferenceCodeSetId?: number;
  ReferenceCodeSetName?: string;
  CodeName: string;
  CodeDesc?: string;
  CodeAlphaValue?: string | null;
  CodeNumericalValue?: number | null;
  CodeDateValue?: string | null;
}

@Injectable({ providedIn: 'root' })
export class HubService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private sharedData: SharedDataService
  ) {}

  /**
   * Returns the full list of storage hubs.
   * Cached in SharedDataService — call clearStorageHubCache() after add/update.
   */
  getStorageHubs(): Observable<StorageHub[]> {
    return this.sharedData.getStorageHubs();
  }

  /**
   * Add a new storage hub and invalidate the hub cache.
   */
  addStorageHub(payload: StorageHubPayload): Observable<any> {
    const url = `${this.baseUrl}/${MATTRACKERAPIConstants.ADD_STORAGE_HUB}`;
    return this.http.post(url, payload).pipe(
      tap(() => this.sharedData.clearStorageHubCache())
    );
  }

  /**
   * Update an existing storage hub and invalidate the hub cache.
   */
  updateStorageHub(payload: StorageHubPayload): Observable<any> {
    const url = `${this.baseUrl}/${MATTRACKERAPIConstants.UPDATE_STORAGE_HUB}`;
    return this.http.put(url, payload).pipe(
      tap(() => this.sharedData.clearStorageHubCache())
    );
  }

  /**
   * Returns the Location_Type reference codes used in the Add Hub modal.
   * Cached in SharedDataService — this data only changes via a deployment.
   */
  getLookupData(codeSetName: string): Observable<LookupData[]> {
    // Only Location_Type is currently used; route it through the cache.
    // Other codeSetNames fall back to a direct HTTP call.
    if (codeSetName === 'Location_Type') {
      return this.sharedData.getHubLocationTypes();
    }
    const url = `${this.baseUrl}/${MATTRACKERAPIConstants.GET_LOOKUP_DATA}?codeSetName=${codeSetName}`;
    return this.http.get<any>(url).pipe(
      map(response => response.data || [])
    );
  }
}
